import { useRef } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

/**
 * Pixels the pointer may travel between press and release and still count as a
 * click rather than a drag.
 *
 * Deliberately small: a genuine click on a trackpad or a touchscreen almost
 * always registers 1-3px of jitter, while an intentional drag crosses this
 * within the first frame. Matching react-slick's own `touchThreshold` would be
 * far too permissive — that value gates whether the carousel PAGES, and a drag
 * released below it still moved the card visibly under the cursor, which is
 * exactly the case that must not navigate.
 */
const DRAG_THRESHOLD_PX = 5

/**
 * Stops a drag from being treated as a click on a link or button.
 *
 * The problem this solves: inside a draggable carousel, releasing the pointer
 * mid-swipe still fires the anchor's click, so letting go halfway through a
 * slide navigates instead of settling the carousel. The browser has no notion
 * of "this mouseup ended a drag" — a click is simply a press and a release on
 * the same element, however far the pointer travelled in between.
 *
 * So the distance is measured here: record where the press landed, and on the
 * click compare against where the release did. Past the threshold the gesture
 * was a drag, and the click is suppressed.
 *
 * Returns props to spread onto the element. `onClick` runs the caller's
 * handler only for real clicks; navigation-by-href is cancelled with
 * `preventDefault`, which is what makes this work for a plain `<Link>` that
 * has no handler of its own.
 *
 * ```tsx
 * const dragAware = useDragAwareClick()
 * <Link to={href} {...dragAware} draggable={false} />
 * ```
 */
export const useDragAwareClick = (onClick?: (e: ReactMouseEvent) => void) => {
  'use memo'

  // A ref, not state: these are written during a gesture and read on its
  // click. Re-rendering the card on every press would be pointless work, and
  // state would not have settled by the time the click handler reads it.
  const origin = useRef<{ x: number; y: number } | null>(null)

  const handleMouseDown = (e: ReactMouseEvent) => {
    origin.current = { x: e.clientX, y: e.clientY }
  }

  const handleClick = (e: ReactMouseEvent) => {
    const start = origin.current
    origin.current = null

    if (start) {
      const movedX = Math.abs(e.clientX - start.x)
      const movedY = Math.abs(e.clientY - start.y)
      if (movedX > DRAG_THRESHOLD_PX || movedY > DRAG_THRESHOLD_PX) {
        // The gesture was a drag. Cancel the anchor's own navigation as well
        // as the caller's handler — for a bare <Link> the href IS the
        // behaviour, so returning early would still navigate.
        e.preventDefault()
        e.stopPropagation()
        return
      }
    }

    onClick?.(e)
  }

  return { onMouseDown: handleMouseDown, onClick: handleClick }
}

export default useDragAwareClick
